import * as React from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { cn } from '../lib/utils';

export type AreaTrendPoint = {
  timestamp: string;
  [metric: string]: number | string;
};

export type AreaTrendSeries = {
  key: string;
  label: string;
  color?: string;
};

export type AreaTrendChartProps = {
  data: AreaTrendPoint[];
  series: AreaTrendSeries[];
  className?: string;
  yAxisLabel?: string;
  valueFormatter?: (value: number, key: string) => string;
};

const fallbackPalette = [
  'hsl(var(--primary))',
  'hsl(var(--status-info))',
  'hsl(var(--status-warning))',
  'hsl(var(--status-error))',
] as const;

const timestampFormatter = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat('en', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
};

const defaultValueFormatter = (value: number) =>
  Number.isFinite(value) ? Number(value.toFixed(2)).toString() : '0';

export function AreaTrendChart({
  data,
  series,
  className,
  yAxisLabel,
  valueFormatter = defaultValueFormatter,
}: AreaTrendChartProps) {
  return (
    <div className={cn('h-56 w-full', className)}>
      <ResponsiveContainer>
        <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis
            dataKey="timestamp"
            tickFormatter={timestampFormatter}
            stroke="hsl(var(--muted-foreground))"
            tickLine={false}
            axisLine={false}
            minTickGap={24}
          />
          <YAxis
            stroke="hsl(var(--muted-foreground))"
            tickLine={false}
            axisLine={false}
            width={52}
            tickFormatter={(value: string | number) => defaultValueFormatter(Number(value))}
            label={
              yAxisLabel
                ? {
                    value: yAxisLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: { fill: 'hsl(var(--muted-foreground))', fontSize: 12 },
                  }
                : undefined
            }
          />
          <Tooltip
            formatter={(value: unknown, key: string) => valueFormatter(Number(value), key)}
            labelFormatter={(label: string) => timestampFormatter(label)}
            contentStyle={{
              backgroundColor: 'hsl(var(--background))',
              borderColor: 'hsl(var(--border))',
              borderRadius: 8,
            }}
          />
          {series.map((item, index) => {
            const color = item.color ?? fallbackPalette[index % fallbackPalette.length];
            return (
              <Area
                key={item.key}
                type="monotone"
                dataKey={item.key}
                name={item.label}
                stroke={color}
                fill={color}
                fillOpacity={0.14}
                strokeWidth={2}
                isAnimationActive={false}
              />
            );
          })}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
